import { makeAPICall, updateBreadcrumbs } from '@gokwik/utilities'
import { useEffect, useState } from 'react'
import Configs from './configs'

import {
    Config,
    ConfigResponse,
    PaymentMethodsObject,
    PaymentPageConfig,
    SelectDropdown,
    SimluatorConfigObject,
    SubCategories,
    Options,
} from '@library/utilities/interface'
import { useSelector } from 'react-redux'
import { getMerchantConfig, getMerchantDetails, isMerchantSelector } from '@store/user/selectors'
import { Alert, Button, message, Modal, Row, Select } from 'gokwik-ui-kit'
import APIEndPoints from '@library/utilities/constants/apiEndpoints'
import {
    InstrumentNameMapping,
    MerchantConfigKeys,
    PAYMENT_METHODS,
    Platforms,
    SuccessStatusCodes,
} from '@library/utilities/constants/constants'
import { deepCopy, deepEqual } from '@library/utilities/helpers/helper'
import { handleError } from '@library/utilities/helpers/handleError'
import ConfigDifferences from './configDifferences'
import Simulator from '@library/components/simulator/simulator'
import OldConfigsScreen from './oldConfigsScreen'

const Index = () => {
    const merchantData = useSelector(getMerchantDetails)
    const isMerchantUser = useSelector(isMerchantSelector)
    const showNewConfigsPage = useSelector(getMerchantConfig(MerchantConfigKeys.paymentConfigsEnabled))
    const [configData, setConfigData] = useState<PaymentPageConfig[]>([])
    const [simulatorInfo, setSimulatorInfo] = useState<SimluatorConfigObject>()
    const [originalData, setOriginalData] = useState<PaymentPageConfig[]>([])
    const [platform, setPlatform] = useState<string>(Platforms.CHECKOUT_1_0_SHOPIFY)
    const [platformOptions, setPlatformOptions] = useState<SelectDropdown[]>([])
    const [updatedConfigs, setUpdatedConfigs] = useState<Config[]>([])
    const [modalOpen, setModalOpen] = useState<any>()
    const [showNewConfigsScreen, setShowNewConfigsScreen] = useState<boolean>(false)
    const [showNoSupportBanner, setShowNoSupportBanner] = useState<boolean>(false)
    const [disableRevertBtn, setDisableRevertBtn] = useState<boolean>(true)

    useEffect(() => {
        updateBreadcrumbs((prev) => [
            prev[0],
            {
                key: 'settings',
                href: '/payment/settings',
                text: 'Payment Settings',
            },
            {
                key: 'configs',
                href: '/payment/settings/configs',
                text: 'Configs',
            },
        ])
    }, [])

    useEffect(() => {
        // If merchant user & config flag is enabled then show new configs page
        if (isMerchantUser && showNewConfigsPage) {
            setShowNewConfigsScreen(true)
        }
        // For Admin user show the new configs page
        if (!isMerchantUser) {
            setShowNewConfigsScreen(true)
            // Disable the actions and show the no support banner if the config flag is disabled
            if (!showNewConfigsPage) {
                setShowNoSupportBanner(true)
            }
        }
        getPlatforms()
    }, [showNewConfigsPage, isMerchantUser])

    async function getPlatforms() {
        if (!merchantData?.m_id) {
            message.error('Merchant is required')
            return
        }
        try {
            let response = await makeAPICall({
                method: 'get',
                url: process.env.REACT_APP_AUX_BASE_URL + APIEndPoints.paymentConfigs.getPlatforms(merchantData?.m_id),
            })
            if (SuccessStatusCodes.includes(response?.status)) {
                const platforms = response?.data?.data ?? []
                if (platforms.length === 0) return
                const data = platforms.map((platform) => ({
                    label: getPlatformName(platform),
                    value: platform,
                }))
                setPlatformOptions(data)
                setPlatform(data[0].value)
            } else {
                const errMessage = response?.response?.data?.error?.reference?.message ?? 'Failed to fetch platforms'
                message.error(errMessage)
            }
        } catch (error) {
            handleError(error)
        }
    }

    useEffect(() => {
        setConfigData([]);
        getPaymentsConfig(platform)
    }, [platform])

    async function getPaymentsConfig(platform: string) {
        if (!merchantData?.m_id) {
            message.error('Merchant is required')
            return
        }
        try {
            let response = await makeAPICall({
                method: 'get',
                url:
                    process.env.REACT_APP_AUX_BASE_URL +
                    APIEndPoints.paymentConfigs.getConfigs(merchantData?.m_id, platform),
                skipMode: true,
            })
            if (SuccessStatusCodes.includes(response?.status)) {
                const data = updateResponse(response?.data?.data ?? [])
                const clonedData = deepCopy(data)
                setConfigData(data)
                setOriginalData(clonedData)
            } else {
                setConfigData([])
                setOriginalData([])
                const errMessage = response?.response?.data?.error?.reference?.message ?? 'Failed to fetch configs'
                message.error(errMessage)
            }
            setDisableRevertBtn(true)
        } catch (error) {
            handleError(error)
        }
    }

    const getTheDifferences = (originalData: PaymentPageConfig[], updatedData: PaymentPageConfig[]) => {
        try {
            let differences = []
            originalData.forEach((originalCategory, i) => {
                Object.entries(originalCategory.configs).forEach(([key, originalConfig]) => {
                    originalConfig.forEach((config, index) => {
                        if (
                            typeof config.value === 'object' ||
                            typeof updatedData[i].configs[key][index].value === 'object'
                        ) {
                            if (!deepEqual(config.value, updatedData[i].configs[key][index].value)) {
                                const data = { ...updatedData[i].configs[key][index], oldValue: config.value }
                                differences.push(data)
                            }
                        } else if (config.value !== updatedData[i].configs[key][index].value) {
                            const data = { ...updatedData[i].configs[key][index], oldValue: config.value }
                            differences.push(data)
                        }
                    })
                })
            })
            return differences
        } catch (err) {}
    }

    const confirmChanges = () => {
        const differences = getTheDifferences(originalData, configData)
        if (differences.length === 0) message.info('No changes were made to the configs')
        setUpdatedConfigs(differences)
    }

    async function updateConfigs() {
        if (!merchantData?.m_id) {
            message.error('Merchant is required')
            return
        }
        setUpdatedConfigs([])
        try {
            if (updatedConfigs.length < 1) return
            const payload = updatedConfigs.map((config) => ({
                blueprint_id: config.blueprint_id,
                value: config.value,
            }))
            let response = await makeAPICall({
                method: 'patch',
                url: process.env.REACT_APP_AUX_BASE_URL + APIEndPoints.paymentConfigs.updateConfigs,
                payload: {
                    merchant_id: merchantData?.m_id,
                    configs: payload,
                },
            })
            if (SuccessStatusCodes.includes(response?.data?.status_code)) {
                message.success('Configs updated successfully')
                getPaymentsConfig(platform)
            } else {
                const errMessage =
                    (response?.response?.data?.error?.reference?.message || response?.response?.data?.error?.message) ??
                    'Failed to update Configs'
                message.error(errMessage)
            }
        } catch (error) {
            handleError(error)
        }
    }

    const updateResponse = (categories: ConfigResponse[]): PaymentPageConfig[] => {
        let categoriesData = []
        for (let [categoryName, categoryVal] of Object.entries(categories)) {
            const subcategoriesObj = categories[categoryName].subcategories as SubCategories
            let subcategoriesData = {}
            for (let [subCategory, subCategoryVal] of Object.entries(subcategoriesObj)) {
                const configsInfo = []
                for (let [config, configVal] of Object.entries(subCategoryVal.configs)) {
                    configsInfo.push({
                        name: config,
                        ...configVal,
                        category: categoryName ?? '',
                        categoryTitle: categoryVal?.title ?? '',
                        categoryDescription: categoryVal?.description ?? '',
                        subcategory: subCategory ?? '',
                        subcategoryTitle: subCategoryVal?.title ?? '',
                        subcategoryDescription: subCategoryVal?.description ?? '',
                        platform: platform ?? '',
                    })
                }
                subcategoriesData = { ...subcategoriesData, [subCategory]: configsInfo }
            }
            categoriesData.push({
                description: categoryVal.description,
                name: categoryName,
                configs: subcategoriesData,
                title: categoryVal.title,
            })
        }
        return categoriesData
    }

    const updateConfigValue = (config: Config, value: any) => {
        const stateData = configData.map((data) => {
            if (data.name === config.category && Object.keys(data.configs).includes(config.subcategory)) {
                if (Array.isArray(data.configs[config.subcategory])) {
                    data.configs[config.subcategory] = data.configs[config.subcategory].map((configData: Config) =>
                        config.blueprint_id === configData.blueprint_id ? { ...configData, value } : configData,
                    )
                }
            }
            return data
        })
        const differences = getTheDifferences(originalData, stateData)
        const isRevertDisabled = differences.length === 0
        setDisableRevertBtn(isRevertDisabled)
        setConfigData(stateData)
    }

    useEffect(() => {
        setSimulatorConfigs(configData)
    }, [configData])

    function setSimulatorConfigs(configData: PaymentPageConfig[]) {
        try {
            const simulatorData = {
                showPrice: false,
                showComparedPrice: false,
                pageTitle: '',
                paymentButtonsStyle: '',
                showDecimalValue: false,
                methodPositions: [],
                paymentMethods: [],
                isEnabled: false,
                pageTitleColor: '',
            }
            let enabledMethods = []
            let allAvailableMethods = []

            const generalConfig = configData?.find((config) => config.name === 'payment_page')?.configs?.['general']
            generalConfig?.forEach((config) => {
                if (config.name === 'show_price') simulatorData.showPrice = config.value as boolean
                if (config.name === 'show_compared_price') simulatorData.showComparedPrice = config.value as boolean
                if (config.name === 'page_title') simulatorData.pageTitle = config.value as string
                if (config.name === 'page_style') simulatorData.paymentButtonsStyle = config.value as string
                if (config.name === 'show_decimal_value') simulatorData.showDecimalValue = config.value as boolean
                if (config.name === 'enabled_methods') {
                    allAvailableMethods = (config?.validations as Options)?.options as Array<string>
                    enabledMethods = config.value as Array<string>
                }
                if (config.name === 'page_title_color') simulatorData.pageTitleColor = config.value as string
                if (config.name === 'method_positions') simulatorData.methodPositions = config.value as Array<string>
            })

            // Process payment methods based on their positions and configurations
            if (simulatorData.methodPositions.length > 0) {
                const methodConfigs = configData?.find((config) => config.name === 'method_ux_controls')
                const paymentMethods = simulatorData.methodPositions?.map((val) => {
                    const configs =
                        methodConfigs?.configs[val === PAYMENT_METHODS.SIMPLPAYIN3 ? PAYMENT_METHODS.PAY_IN_X : val]
                    return parseConfigs(configs, val, enabledMethods)
                })
                simulatorData.paymentMethods = paymentMethods
            }

            //check if any bnpl method is enabled
            const bnplMethod = simulatorData.paymentMethods.find((method) => method.name.includes(PAYMENT_METHODS.BNPL))
            if (bnplMethod) {
                // If bnpl-gk is enabled the add it as a seperate method
                if (enabledMethods.includes(PAYMENT_METHODS.GOKWIK_BNPL)) {
                    // keep the bnpl-gk in same position as bnpl method
                    const bnplIndex = simulatorData.paymentMethods.findIndex(
                        (method) => method.name === PAYMENT_METHODS.BNPL,
                    )
                    simulatorData.paymentMethods.splice(bnplIndex, 0, {
                        ...bnplMethod,
                        name: PAYMENT_METHODS.GOKWIK_BNPL,
                        label: 'Pay Later on UPI',
                        subtext: '0% interest & No hidden charges',
                        isEnabled: true,
                    })
                }
                // Handle single BNPL method (excluding bnpl-gk)
                const bnplMethods = enabledMethods.filter(
                    (method) => method.includes(PAYMENT_METHODS.BNPL) && method !== PAYMENT_METHODS.GOKWIK_BNPL,
                )
                if (bnplMethods.length === 0) {
                    simulatorData.paymentMethods = simulatorData.paymentMethods.filter(
                        (method) => method.name !== PAYMENT_METHODS.BNPL,
                    )
                } else if (bnplMethods.length === 1) {
                    const bnplMethod = bnplMethods[0]
                    simulatorData.paymentMethods = simulatorData.paymentMethods.map((method) =>
                        method.name.includes(PAYMENT_METHODS.BNPL) && method.name !== PAYMENT_METHODS.GOKWIK_BNPL
                            ? { ...method, name: bnplMethod, label: InstrumentNameMapping[bnplMethod], isEnabled: true }
                            : method,
                    )
                }
            }

            // Add native method from 'method_ux_controls'
            const methodUxControls = configData?.find((config) => config.name === 'method_ux_controls')
            const nativeMethod = methodUxControls?.configs?.[PAYMENT_METHODS.NATIVE]
            if (nativeMethod) {
                const nativeMethodDetails = parseConfigs(nativeMethod, PAYMENT_METHODS.NATIVE, enabledMethods)
                simulatorData.paymentMethods.push(nativeMethodDetails)
            }

            // If ppcod is enabled enabled then add it as a seperate method and remove cod from simulatorData.paymentMethods
            const ppcodConfig = methodUxControls?.configs?.[PAYMENT_METHODS.PPCOD] ?? []
            const isPPCODEnabled = ppcodConfig?.find((config) => config.name === 'enable_ppcod')?.value as boolean
            if (!!isPPCODEnabled) {
                const ppcodDetails = parseConfigs(ppcodConfig, PAYMENT_METHODS.PPCOD, enabledMethods)
                const codIndex = simulatorData.paymentMethods.findIndex((method) => method.name === PAYMENT_METHODS.COD)
                simulatorData.paymentMethods.splice(codIndex, 0, ppcodDetails)
                simulatorData.paymentMethods = simulatorData.paymentMethods.filter(
                    (method) => method.name !== PAYMENT_METHODS.COD,
                )
            }
            setSimulatorInfo(simulatorData)
        } catch (error) {}
    }

    function checkIfExist(positionMethodName: string, enabledMethodName: string) {
        if (positionMethodName === PAYMENT_METHODS.CARDS) {
            return enabledMethodName === 'cc' || enabledMethodName === 'dc'
        } else if (positionMethodName === PAYMENT_METHODS.BHARATX) {
            return enabledMethodName === PAYMENT_METHODS.BHARATX_PAY
        } else if (positionMethodName === PAYMENT_METHODS.EMI) {
            return /^emi-/.test(enabledMethodName)
        } else if (positionMethodName === PAYMENT_METHODS.BNPL) {
            return /^bnpl-/.test(enabledMethodName)
        } else if (positionMethodName === PAYMENT_METHODS.WALLETS) {
            return /^wallet-/.test(enabledMethodName)
        } else if (positionMethodName === PAYMENT_METHODS.SIMPLPAYIN3) {
            return enabledMethodName.includes(PAYMENT_METHODS.SIMPLPAYIN3)
        } else {
            return enabledMethodName.includes(positionMethodName)
        }
    }

    function parseConfigs(configGroup: Config[], subcategory, enabledMethods = []): PaymentMethodsObject {
        const configObject: PaymentMethodsObject = {
            name: subcategory,
            isEnabled: enabledMethods.some((method) => checkIfExist(subcategory, method)),
        }
        configGroup?.forEach((config) => {
            switch (config.name) {
                case 'button_title':
                    configObject.label = config.value as string
                    break
                case 'button_subtext':
                    configObject.subtext = config.value as string
                    break
                case 'button_color':
                    configObject.color = config.value as string
                    break
                case 'button_bg_color':
                    configObject.backgroundColor = config.value as string
                    break
                case 'button_badge_text':
                    configObject.badgeText = config.value as string
                    break
                case 'button_badge_bg_color':
                    configObject.badgeBgColor = config.value as string
                    break
                case 'button_badge_color':
                    configObject.badgeColor = config.value as string
                    break
                case 'enable_preferred_panel':
                    configObject.showPreferredPanel = config.value as boolean
                    break
                case 'enable_native_redirection':
                case 'enable_ppcod':
                    configObject.isEnabled = config.value as boolean
                    break
                default:
                    break
            }
        })
        return configObject
    }

    const restoreValuesToDefault = () => {
        const differences = getTheDifferences(originalData, configData)
        if (differences.length === 0) {
            message.info('No changes were made to the configs')
            return
        }
        setDisableRevertBtn(true)
        setModalOpen(differences)
    }

    const resetValues = () => {
        const oldData = deepCopy(originalData)
        setConfigData(oldData)
    }

    const getPlatformName = (platform: string) => {
        switch (platform) {
            case Platforms.CHECKOUT_1_0_SHOPIFY:
                return 'Checkout 1.0';
            case Platforms.CORE:
                return 'Core';
            case Platforms.GKP:
                return 'GKP';
            case Platforms.SHOPIFY_PAYMENTS_APP:
                return 'Shopify App';
            default:
                return platform;
        }
    }

    return (
        <>
            {showNewConfigsScreen ? (
                <>
                    <div className='w-full h-full box-border flex gap-4 flex-column' style={{ overflowY: 'auto', maxHeight: '100vh' }}>
                        <div className='configs overflow-auto border border-gray-300 w-full' style={{ maxHeight: 'calc(100vh - 100px)' }}>
                            <div className='flex justify-between items-center box-border'>
                                <p className='text-xl font-semibold'>Payment Configuration</p>
                                <Select
                                    placeholder='Select platform'
                                    onChange={(value) => setPlatform(value)}
                                    options={platformOptions}
                                    value={platform}
                                />
                            </div>
                            {showNoSupportBanner && (
                                <Alert
                                    className='mt-3'
                                    showIcon
                                    message={
                                        <p className='p-2'>
                                            This merchant has not yet been migrated to the new config system. Any
                                            changes made here will not be reflected at checkout. Please use the Admin
                                            portal to update the configurations
                                        </p>
                                    }
                                    type='info'
                                />
                            )}
                            <Configs
                                configData={configData ?? []}
                                updateConfigValue={updateConfigValue}
                                handleConfirm={confirmChanges}
                                restoreValuesToDefault={restoreValuesToDefault}
                                disableActions={showNoSupportBanner}
                                disableRevert={disableRevertBtn}
                                platform={platform}
                            />
                        </div>
                        {platform === Platforms.CHECKOUT_1_0_SHOPIFY && (
                            <div
                                className='simulator sticky right-0 bottom-0 bg-white p-1 h-full overflow-hidden'
                                style={{ maxWidth: '440px', minWidth: '320px' }}
                            >
                                <Simulator simulatorInfo={simulatorInfo} />
                            </div>
                        )}
                    </div>

                    {updatedConfigs?.length > 0 && (
                        <ConfigDifferences
                            handleClose={() => setUpdatedConfigs([])}
                            differenceData={updatedConfigs}
                            saveDetails={updateConfigs}
                        />
                    )}

                    <Modal
                        open={modalOpen ? true : false}
                        onOk={() => {
                            resetValues()
                            setModalOpen(null)
                        }}
                        onCancel={() => {
                            setModalOpen(null)
                        }}
                        footer={() => (
                            <Row className='flex justify-end gap-2'>
                                <Button onClick={() => setModalOpen(null)} variant='default'>
                                    Cancel
                                </Button>
                                <Button
                                    onClick={() => {
                                        resetValues()
                                        setModalOpen(null)
                                    }}
                                    variant='primary'
                                >
                                    Confirm
                                </Button>
                            </Row>
                        )}
                        centered
                        width={'400px'}
                    >
                        <p className='p-2'>Are you sure you want to revert the changes you made?</p>
                    </Modal>
                </>
            ) : (
                <OldConfigsScreen />
            )}
        </>
    )
}
export default Index
